'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import ToolFormControl from 'DesignComponents/tool/ToolFormControl';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import ToolFieldset from 'DesignComponents/tool/ToolFieldset';
import ToolFormLabel from 'DesignComponents/tool/ToolFormLabel';
import SidebarInput from 'DesignEditorsUI/components/shared/sidebar/SidebarInput';
import UrlField from 'ContentComponents/cmv2/fields/Url';
import LinkField from 'ContentComponents/cmv2/fields/Link';
import UISelect from 'UIComponents/input/UISelect';
import UIToggle from 'UIComponents/input/UIToggle';
import UIAlert from 'UIComponents/alert/UIAlert';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIIcon from 'UIComponents/icon/UIIcon';
import { MARIGOLD } from 'HubStyleTokens/colors';
import { DEFAULT, SHOW_ADVANCED_REL_OPTIONS, SUPPORTED_TYPES } from 'DesignData/enums/CustomWidgetFieldAttributes';
import UrlTypes from 'ContentUtils/enums/UrlTypes';
import { LINK, URL } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import ModuleFieldMap from 'DesignEditorsUI/lib/ModuleFieldMap';
import { getHasDismissedAdvancedRelOptionsWarning } from 'DesignData/selectors/app';
import { getFieldCreateOptions } from 'DesignData/selectors/customWidgetEditorOptions';
import { dismissAdvancedRelOptionsWarning } from 'DesignData/actions/Application';

function mapStateToProps(state, props) {
  const {
    urlSupportedTypePermissions
  } = getFieldCreateOptions(state, props);
  return {
    hasDismissedAdvancedRelOptionsWarning: getHasDismissedAdvancedRelOptionsWarning(state),
    urlSupportedTypePermissions
  };
}

const mapDispatchToProps = {
  dismissAdvancedRelOptionsWarning
};

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.contentOptions.${key}`;

class UrlContentOptions extends PureComponent {
  constructor(...args) {
    super(...args);

    this.getTypeOptions = () => {
      const {
        field,
        urlSupportedTypePermissions = {}
      } = this.props;
      return Object.keys(UrlTypes).map(type => {
        const isMissingPermissions = urlSupportedTypePermissions[type] === false;
        const isSelected = field[SUPPORTED_TYPES].includes(type);
        return {
          showMissingPermissionsTooltip: isMissingPermissions,
          disabled: isMissingPermissions && !isSelected,
          badge: isMissingPermissions && isSelected && /*#__PURE__*/_jsx(UIIcon, {
            color: MARIGOLD,
            name: "warning"
          }),
          value: type,
          text: I18n.text(`ContentComponents.cmv2.fields.url.linkType.linkLabels.${type}`)
        };
      });
    };

    this.onFieldChange = value => {
      this.props.editField({
        [DEFAULT]: value
      });
    };

    this.onAcceptedTypesChange = ({
      target: {
        value
      }
    }) => {
      this.props.editField({
        [SUPPORTED_TYPES]: value
      });
    };

    this.onShowAdvancedRelOptionsChange = ({
      target: {
        checked
      }
    }) => {
      this.props.editField({
        [SHOW_ADVANCED_REL_OPTIONS]: checked
      });
    };

    this.onAdvancedRelWarningClose = () => {
      this.props.dismissAdvancedRelOptionsWarning();
    };

    this.renderOption = (_ref) => {
      let {
        children,
        option
      } = _ref,
          rest = _objectWithoutPropertiesLoose(_ref, ["children", "option"]);

      return /*#__PURE__*/_jsx("span", Object.assign({}, rest, {
        children: /*#__PURE__*/_jsx(UITooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('url.disabledUrlType')
          }),
          disabled: !option.showMissingPermissionsTooltip,
          children: children
        })
      }));
    };
  }

  renderLinkOrUrlField() {
    const {
      field,
      fieldErrors,
      readOnly
    } = this.props;
    const FieldComponent = field.type === URL ? UrlField : LinkField;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`defaultLabels.${field.type}`)
      }),
      error: fieldErrors[DEFAULT],
      children: /*#__PURE__*/_jsx(FieldComponent, {
        onFieldChange: this.onFieldChange,
        componentsByType: ModuleFieldMap,
        value: field[DEFAULT],
        show_advanced_rel_options: field.type === LINK && field[SHOW_ADVANCED_REL_OPTIONS],
        supported_types: field[SUPPORTED_TYPES],
        readOnly: readOnly
      })
    });
  }

  renderSupportedTypesSelect() {
    const {
      field
    } = this.props;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('url.acceptedTypesSelect.label')
      }),
      children: /*#__PURE__*/_jsx(UISelect, {
        multi: true,
        value: field[SUPPORTED_TYPES],
        defaultValue: Object.values(UrlTypes || {}),
        anchorType: "button",
        onChange: this.onAcceptedTypesChange,
        options: this.getTypeOptions(),
        itemComponent: this.renderOption,
        placeholder: I18n.text(i18nKey('url.acceptedTypesSelect.placeholder'))
      })
    });
  }

  renderShowAdvancedRelOptionsToggle() {
    const {
      field,
      hasDismissedAdvancedRelOptionsWarning,
      readOnly
    } = this.props;
    if (field.type === URL) return null;
    const showAdvancedRelWarning = !hasDismissedAdvancedRelOptionsWarning && field[SHOW_ADVANCED_REL_OPTIONS];
    return /*#__PURE__*/_jsxs("div", {
      className: "m-top-4",
      children: [/*#__PURE__*/_jsx(ToolFormLabel, {
        className: "p-right-4 p-top-1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('url.advancedRelOptions')
        })
      }), /*#__PURE__*/_jsx(SidebarInput, {
        checked: field[SHOW_ADVANCED_REL_OPTIONS],
        disabled: readOnly,
        inputComponent: UIToggle,
        onChange: this.onShowAdvancedRelOptionsChange,
        size: "extra-small"
      }), showAdvancedRelWarning && /*#__PURE__*/_jsx(UIAlert, {
        className: "m-top-3",
        type: "warning",
        closeable: true,
        onClose: this.onAdvancedRelWarningClose,
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey('url.advancedRelOptionsWarning'),
          options: {
            relProperty: /*#__PURE__*/_jsx("code", {
              children: "rel"
            })
          }
        })
      })]
    });
  }

  render() {
    return /*#__PURE__*/_jsxs(ToolFieldset, {
      children: [this.renderLinkOrUrlField(), this.renderSupportedTypesSelect(), this.renderShowAdvancedRelOptionsToggle()]
    });
  }

}

UrlContentOptions.propTypes = {
  dismissAdvancedRelOptionsWarning: PropTypes.func,
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  hasDismissedAdvancedRelOptionsWarning: PropTypes.bool,
  readOnly: PropTypes.bool,
  fieldErrors: PropTypes.object,
  urlSupportedTypePermissions: PropTypes.object.isRequired
};
UrlContentOptions.defaultProps = {
  inputProps: {},
  formControlProps: {}
};
export default connect(mapStateToProps, mapDispatchToProps)(UrlContentOptions);